<script setup>
// PIN check-in body: code slots + on-screen keypad.
// With a `submit` handler (live pass) the entered code is sent to the backend
// and the SERVER decides — validation must never happen client-side. Without
// it (mock demo) the code is compared locally against the spec's pin.
// A wrong full-length code flashes/shakes the slots and clears after 650ms.
// Port of `PinBody` in the source `_shared/CheckInSheet.tsx`.
import { ref, computed, onUnmounted } from 'vue'
import { computeCheckInAvailability } from './checkin'
import BlockedNotice from './BlockedNotice.vue'

const props = defineProps({
  spec: { type: Object, required: true },
  // Live validation: async ({ pinNumber }) => { ok, message }. The verdict is
  // ALWAYS the server's; local compare remains only for the mock demo.
  submit: { type: Function, default: null },
})

const emit = defineEmits(['success'])

const len = computed(() => props.spec.pinLength ?? String(props.spec.pin ?? '').length)
const val = ref('')
const error = ref(false)
const attempts = ref(0)
const submitting = ref(false)
const serverError = ref('')
const availability = computed(() => computeCheckInAvailability(props.spec.details))
const blocked = computed(() => !availability.value.ok)

const expected = computed(() => String(props.spec.pin ?? '').toUpperCase())

// The line above the keypad shows the partner-editable How To Use value
// (single source of truth with the details panel); hint is the fallback.
const hintText = computed(() => {
  const steps = props.spec.details?.howToUse
  return steps?.length ? steps.join(' ') : props.spec.hint
})

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del']

let successTimer = null
let clearTimer = null

// Wrong code: shake/flash the slots, then clear so they can retry.
const fail = () => {
  error.value = true
  attempts.value += 1
  clearTimer = setTimeout(() => {
    val.value = ''
  }, 650)
}

const press = (k) => {
  if (submitting.value) return
  if (k === 'del') {
    error.value = false
    val.value = val.value.slice(0, -1)
    return
  }
  if (val.value.length >= len.value) return
  // Clear a stale error the moment they start a new attempt.
  if (error.value) error.value = false
  const next = val.value + k
  val.value = next
  if (next.length !== len.value) return

  if (props.submit) {
    submitting.value = true
    serverError.value = ''
    props.submit({ pinNumber: next })
      .then((result) => {
        if (result?.ok) {
          successTimer = setTimeout(() => emit('success'), 250)
          return
        }
        serverError.value = result?.message || ''
        fail()
      })
      .catch(() => fail())
      .finally(() => {
        submitting.value = false
      })
    return
  }

  if (next.toUpperCase() === expected.value) {
    successTimer = setTimeout(() => emit('success'), 250)
  } else {
    fail()
  }
}

onUnmounted(() => {
  clearTimeout(successTimer)
  clearTimeout(clearTimer)
})
</script>

<template>
  <div class="cs-pin">
    <BlockedNotice :availability="availability" />
    <div v-if="hintText && !blocked" class="cs-pin__hint">{{ hintText }}</div>

    <!-- Slots -->
    <div class="cs-pin__slots" :class="{ 'cs-pin__slots--blocked': blocked, 'cs-pin-shake': error }">
      <div
        v-for="(_, i) in len"
        :key="i"
        class="cs-pin__slot"
        :class="{
          'cs-pin__slot--error': error,
          'cs-pin__slot--active': !error && i === val.length,
        }"
      >
        {{ val[i] ?? '' }}
      </div>
    </div>

    <div v-if="error" class="cs-pin__error">
      {{ serverError || (attempts >= 3
        ? "Still no match. Ask a team member for today's code."
        : "That code didn't match. Try again.") }}
    </div>

    <!-- Keypad -->
    <div class="cs-pin__keypad" :class="{ 'cs-pin__keypad--blocked': blocked }">
      <button
        v-for="(k, i) in KEYS"
        :id="`checkin-pin-key-${k || 'empty'}-${i}`"
        :key="i"
        type="button"
        class="cs-pin__key gtm-click"
        :class="{ 'cs-pin__key--filled': k }"
        :disabled="!k || blocked"
        @click="k && press(k)"
      >
        {{ k === 'del' ? '⌫' : k }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `space-y-3 pt-1 pb-2`
.cs-pin {
  padding-top: 4px; // pt-1
  padding-bottom: 8px; // pb-2

  > * + * {
    margin-top: 12px; // space-y-3
  }

  // Source: `text-center text-[11px] ${theme.muted}`
  &__hint {
    text-align: center;
    font-size: 11px;
    color: var(--fr-muted, #6b7280);
  }

  // Source: `flex justify-center gap-1.5`
  &__slots {
    display: flex;
    justify-content: center;
    gap: 6px; // gap-1.5

    &--blocked {
      opacity: 0.4;
    }
  }

  // Source: `grid h-10 w-9 place-items-center rounded-lg bg-current/5 ring-1
  //          font-mono text-lg font-black`
  &__slot {
    display: grid;
    height: 40px; // h-10
    width: 36px; // w-9
    place-items: center;
    border-radius: 8px; // rounded-lg
    background: cs-current(5%);
    box-shadow: 0 0 0 1px cs-current(10%); // ring-1 ring-current/10
    font-family: $cs-font-mono;
    font-size: 18px; // text-lg
    font-weight: 900; // font-black

    // Source: `ring-current/40` on the active slot
    &--active {
      box-shadow: 0 0 0 1px cs-current(40%);
    }

    // Source: `ring-rose-500/70 bg-rose-500/10` on error
    &--error {
      box-shadow: 0 0 0 1px rgba($cs-rose-500, 0.7);
      background: rgba($cs-rose-500, 0.1);
    }
  }

  // Source: `text-center text-[11px] text-rose-500`
  &__error {
    text-align: center;
    font-size: 11px;
    color: $cs-rose-500;
  }

  // Source: `mx-auto grid max-w-[260px] grid-cols-3 gap-1.5`
  // (mx-auto = horizontal only — a `margin: 0 auto` shorthand would zero the
  // vertical space-y-3 rhythm from the parent's `> * + *` rule.)
  &__keypad {
    margin-left: auto;
    margin-right: auto;
    display: grid;
    max-width: 260px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px; // gap-1.5

    &--blocked {
      opacity: 0.4;
    }
  }

  // Source: `h-10 rounded-lg [bg-current/5 ring-1 ring-current/10 active:bg-current/10]
  //          font-display text-base font-bold disabled:opacity-0`
  &__key {
    @include cs-button-reset;
    height: 40px; // h-10
    border-radius: 8px; // rounded-lg
    font-family: var(--fr-font-display, inherit);
    font-size: 16px; // text-base
    font-weight: 700;
    text-align: center;

    &--filled {
      background: cs-current(5%);
      box-shadow: 0 0 0 1px cs-current(10%);

      &:active {
        background: cs-current(10%);
      }
    }

    &:disabled {
      opacity: 0;
      cursor: default;
    }
  }
}

// Source keyframes `cs-pin-shake-kf` (verbatim)
@keyframes cs-pin-shake-kf {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-6px);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(4px);
  }
}

.cs-pin-shake {
  animation: cs-pin-shake-kf 380ms ease-in-out;
}
</style>
